/**
 * TimesheetReports - PDF report generator for the Time Tracker module
 *
 * Generates 4 classic payroll reports:
 *  1. Details Report       — one row per timesheet entry
 *  2. Master Payroll       — per-employee weekly breakdown (Reg / T12 / Dbl / R&B / Travel)
 *  3. OP Code Summary      — grouped by contract → employee with "ALL" rollup rows
 *  4. Contract Report      — grouped by contract → op code → employee with day grid
 *
 * CA divisions (1004, 1012, 1117) use Sun–Sat week ordering.
 * US divisions (2109, 2110, 2111) use Mon–Sun week ordering.
 * Comments on individual entries surface as day-off notes in the reports.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { dateRequired, dateOrder, validateSchema } from '../../utils/validation';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import TimeTrackerAdminHelpModal from './TimeTrackerAdminHelpModal';
import { drawLetterhead, loadLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';

let _timesheetLetterhead = null;

// ─── constants ───────────────────────────────────────────────────────────────

const REPORT_TYPES = [
  { id: 'details',   label: 'Details' },
  { id: 'master',    label: 'Master Payroll' },
  { id: 'opcode',    label: 'OP Code Summary' },
  { id: 'contract',  label: 'Contract Report' },
];

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// CA: Sun first;  US: Mon first
const CA_DAY_ORDER = [0, 1, 2, 3, 4, 5, 6]; // Sun=0 … Sat=6
const US_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon=1 … Sun=0

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ─── tiny helpers ─────────────────────────────────────────────────────────────

// Parse a date value that may arrive as "2026-02-02", "2026-02-02T00:00:00.000Z",
// or a JS Date object — always interpreted in local time to avoid UTC-offset drift.
function parseLocalDate(d) {
  if (!d) return new Date(NaN);
  if (d instanceof Date) return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // Take only the YYYY-MM-DD portion before any 'T' and force local-time parsing
  const iso = String(d).substring(0, 10);
  const [y, m, day] = iso.split('-').map(Number);
  return new Date(y, m - 1, day);
}

function fmtDate(d) {
  // "22-Feb-2026"
  const dt = parseLocalDate(d);
  return `${dt.getDate()}-${MONTH_ABBR[dt.getMonth()]}-${dt.getFullYear()}`;
}

function fmtDateLong(d) {
  // "Mon Feb 23rd" style for comment lines
  const dt = parseLocalDate(d);
  const day = dt.getDate();
  const suffix = day === 1 || day === 21 || day === 31 ? 'st'
               : day === 2 || day === 22 ? 'nd'
               : day === 3 || day === 23 ? 'rd' : 'th';
  return `${DAY_ABBR[dt.getDay()]} ${MONTH_ABBR[dt.getMonth()]} ${day}${suffix}`;
}

// Build the contract key matching original format: job_number already includes discipline_code
// (e.g. job_number='71665PIP'), so use it directly. Fall back to contract+discipline if missing.
// e.g. "71665PIP" or "71897PIP-81002"
function buildContractKey(r) {
  const base = r.job_number
    || ((r.contract || '') + (r.discipline_code || ''))
    || '(no contract)';
  return r.extra_number ? `${base}-${r.extra_number}` : base;
}

// Truncate text with an ellipsis so it fits within maxWidth mm at the doc's current font
function truncateText(doc, text, maxWidth) {
  if (doc.getTextWidth(text) <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && doc.getTextWidth(t + '\u2026') > maxWidth) {
    t = t.slice(0, -1);
  }
  return t + '\u2026';
}

// Normalise legacy shift codes (D1, D2 → D; A1 → A; N1, N2 → N)
function fmtShift(s) {
  if (!s) return '';
  const upper = String(s).toUpperCase();
  if (upper.startsWith('D')) return 'D';
  if (upper.startsWith('A')) return 'A';
  if (upper.startsWith('N')) return 'N';
  return s;
}

function num(v) {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function fmtNum(v) {
  const n = num(v);
  return n === 0 ? '—' : String(n % 1 === 0 ? n : n);
}

function fmtMoney(v) {
  const n = num(v);
  return n === 0 ? '—' : `$${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

// Build a header string from selected projects / division
function buildHeaderLabel(selectedDivisions, selectedProjects, allDivisions, allProjects) {
  if (selectedProjects.length > 0) {
    const names = selectedProjects
      .map(id => allProjects.find(p => String(p.id) === String(id))?.job_name || id)
      .join(' / ');
    return names;
  }
  if (selectedDivisions.length > 0) {
    const names = selectedDivisions
      .map(id => allDivisions.find(d => String(d.id) === String(id))?.division_name || id)
      .join(' / ');
    return names;
  }
  return 'ALL DIVISIONS';
}

// Determine week-day order for a row based on country_code
function dayOrder(row) {
  return row.country_code === 'US' ? US_DAY_ORDER : CA_DAY_ORDER;
}

// For the Master / Contract reports: given the date range array (sorted),
// map each date to a day-of-week index, respecting week ordering.
function buildDayMap(dates, isUS) {
  // Returns array of { date, dowIdx } where dowIdx is position in the 7-col header
  const order = isUS ? US_DAY_ORDER : CA_DAY_ORDER;
  return dates.map(d => {
    const dt = parseLocalDate(d);
    const dow = dt.getDay(); // 0=Sun … 6=Sat
    const pos = order.indexOf(dow);
    return { date: d, dow, pos: pos === -1 ? 0 : pos };
  });
}

// ─── PDF styling constants ─────────────────────────────────────────────────────

const PAGE_MARGIN = 14;
const FONT_TITLE  = 14;
const FONT_SUB    = 9;
const FONT_BODY   = 8;
const LINE_H      = 5;
const HEADER_BG   = [220, 220, 220]; // light grey
const TOTAL_BG    = [235, 235, 235]; // slightly darker for totals
const RED_BG      = [200, 50, 50];

async function addPageHeader(doc, title, forLabel, dateRange) {
  const pw = doc.internal.pageSize.getWidth();
  let y = 8;
  if (_timesheetLetterhead) {
    y = await drawLetterhead(doc, _timesheetLetterhead, {
      jobNumber: _timesheetLetterhead.context?.job_number,
      jobName: _timesheetLetterhead.context?.job_name,
      clientName: _timesheetLetterhead.context?.client_name,
      projectType: _timesheetLetterhead.context?.project_type,
      omitJobLine: !_timesheetLetterhead.context?.job_number,
      margin: PAGE_MARGIN,
    });
  }
  // Red title bar (timesheet body stays the same below the letterhead)
  doc.setFillColor(...RED_BG);
  doc.rect(0, y, pw, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(FONT_TITLE);
  doc.setFont('helvetica', 'bold');
  doc.text(title, PAGE_MARGIN, y + 7);
  doc.setTextColor(0, 0, 0);

  y += 14;
  doc.setFontSize(FONT_SUB);
  doc.setFont('helvetica', 'bold');
  doc.text(`FOR ${forLabel}`, PAGE_MARGIN, y);
  y += LINE_H;
  doc.setFont('helvetica', 'normal');
  doc.text(dateRange, PAGE_MARGIN, y);
  return y + LINE_H + 2;
}

function addPageNumbers(doc) {
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120);
    doc.text(`-- ${i} of ${totalPages} --`, pw / 2, ph - 6, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }
}

// Draw a grand-totals summary row (Reg T12 Dbl Travel Height R&B Travel$ Bus Meals)
function drawTotalsRow(doc, rows, startY) {
  const totals = { reg: 0, t12: 0, dbl: 0, travel: 0, height: 0, r_and_b: 0, travel_2: 0, bus: 0, meals: 0 };
  rows.forEach(r => {
    totals.reg     += num(r.reg);
    totals.t12     += num(r.t12);
    totals.dbl     += num(r.dbl);
    totals.travel  += num(r.travel);
    totals.height  += num(r.height);
    totals.r_and_b += num(r.r_and_b);
    totals.travel_2+= num(r.travel_2);
    totals.bus     += num(r.bus);
    totals.meals   += num(r.meals);
  });

  const labels = ['Reg','T12','Dbl','Travel','Height','R&B','Travel$','Bus','Meals'];
  const values = [
    fmtNum(totals.reg), fmtNum(totals.t12), fmtNum(totals.dbl), fmtNum(totals.travel),
    fmtNum(totals.height), fmtMoney(totals.r_and_b), fmtMoney(totals.travel_2),
    fmtMoney(totals.bus), fmtMoney(totals.meals),
  ];

  const pw = doc.internal.pageSize.getWidth();
  const colW = (pw - PAGE_MARGIN * 2) / labels.length;

  // Header labels
  doc.setFillColor(...HEADER_BG);
  doc.rect(PAGE_MARGIN, startY, pw - PAGE_MARGIN * 2, LINE_H, 'F');
  doc.setFontSize(FONT_BODY);
  doc.setFont('helvetica', 'bold');
  labels.forEach((l, i) => {
    doc.text(l, PAGE_MARGIN + colW * i + colW / 2, startY + LINE_H - 1, { align: 'center' });
  });

  // Values
  const valY = startY + LINE_H;
  doc.setFillColor(245, 245, 245);
  doc.rect(PAGE_MARGIN, valY, pw - PAGE_MARGIN * 2, LINE_H, 'F');
  doc.setFont('helvetica', 'normal');
  values.forEach((v, i) => {
    if (v !== '—') {
      doc.text(v, PAGE_MARGIN + colW * i + colW / 2, valY + LINE_H - 1, { align: 'center' });
    }
  });

  return valY + LINE_H + 3;
}

// ─── report generators ────────────────────────────────────────────────────────

// 1. DETAILS REPORT
async function generateDetailsReport(rows, forLabel, dateRange) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  let startY = await addPageHeader(doc, 'DETAILS REPORT', forLabel, dateRange);
  startY = drawTotalsRow(doc, rows, startY);

  const columns = [
    { header: 'Date',     dataKey: 'date' },
    { header: 'Empl\nCode', dataKey: 'empl_code' },
    { header: 'Employee Name', dataKey: 'empl_name' },
    { header: 'Trade',    dataKey: 'trade' },
    { header: 'Contract', dataKey: 'contract' },
    { header: 'OP Code',  dataKey: 'op_code' },
    { header: 'Shift',    dataKey: 'shift' },
    { header: 'Reg',      dataKey: 'reg' },
    { header: 'T12',      dataKey: 't12' },
    { header: 'Dbl',      dataKey: 'dbl' },
    { header: 'Travel',   dataKey: 'travel' },
    { header: 'Height',   dataKey: 'height' },
    { header: 'R&B',      dataKey: 'r_and_b' },
    { header: 'Travel$',  dataKey: 'travel_2' },
    { header: 'Bus',      dataKey: 'bus' },
    { header: 'Meals',    dataKey: 'meals' },
  ];

  // Build table body — after every entry that carries a comment, insert a full-width
  // italic sub-row (array syntax enables colSpan across all 16 columns).
  const body = [];
  rows.forEach(r => {
    body.push({
      date:      r.date ? fmtDate(r.date) : '',
      empl_code: r.empl_code || '',
      empl_name: r.empl_name || '',
      trade:     r.trade || '',
      contract:  buildContractKey(r),
      op_code:   r.op_code || '',
      shift:     fmtShift(r.shift),
      reg:       fmtNum(r.reg),
      t12:       fmtNum(r.t12),
      dbl:       fmtNum(r.dbl),
      travel:    fmtNum(r.travel),
      height:    fmtNum(r.height),
      r_and_b:   fmtMoney(r.r_and_b),
      travel_2:  fmtMoney(r.travel_2),
      bus:       fmtMoney(r.bus),
      meals:     fmtMoney(r.meals),
    });
    if (r.comment) {
      body.push([{
        content: `  >> ${r.comment}`,
        colSpan: columns.length,
        styles: {
          fontStyle:   'italic',
          textColor:   [100, 100, 100],
          fillColor:   [248, 248, 248],
          fontSize:    6,
          cellPadding: { top: 0.5, bottom: 1.5, left: 8, right: 2 },
        },
      }]);
    }
  });

  autoTable(doc, {
    startY,
    columns,
    body,
    theme: 'grid',
    styles:      { fontSize: 6.5, cellPadding: 1.5, overflow: 'linebreak' },
    headStyles:  { fillColor: HEADER_BG, textColor: 0, fontStyle: 'bold', fontSize: 7 },
    columnStyles: {
      date:      { cellWidth: 18 },
      empl_code: { cellWidth: 12 },
      empl_name: { cellWidth: 30 },
      trade:     { cellWidth: 14 },
      contract:  { cellWidth: 22 },
      op_code:   { cellWidth: 14 },
      shift:     { cellWidth: 10 },
    },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    pageBreak: 'auto',
  });

  addPageNumbers(doc);
  return doc;
}

// ─── Master Payroll helpers ───────────────────────────────────────────────────

// Group rows by employee, accumulating hours per day-of-week position
function buildEmployeeWeekData(rows, isUS) {
  const order = isUS ? US_DAY_ORDER : CA_DAY_ORDER;
  // emp key → { empl_code, empl_name, trade, days: Map<dow→{reg,t12,dbl,r_and_b,travel_2}>, comments: [{date,text}] }
  const empMap = new Map();

  rows.forEach(r => {
    const key = r.empl_code || r.empl_name;
    if (!empMap.has(key)) {
      empMap.set(key, {
        empl_code: r.empl_code,
        empl_name: r.empl_name,
        trade: r.trade,
        days: new Map(),
        comments: [],
      });
    }
    const emp = empMap.get(key);

    if (r.date) {
      const dt = parseLocalDate(r.date);
      const dow = dt.getDay();
      if (!emp.days.has(dow)) {
        emp.days.set(dow, { reg: 0, t12: 0, dbl: 0, r_and_b: 0, travel_2: 0 });
      }
      const d = emp.days.get(dow);
      d.reg     += num(r.reg);
      d.t12     += num(r.t12);
      d.dbl     += num(r.dbl);
      d.r_and_b += num(r.r_and_b);
      d.travel_2 += num(r.travel_2);
    }

    if (r.comment) {
      emp.comments.push({ date: r.date, text: r.comment });
    }
  });

  return { empMap, order };
}

// Draw a single employee block for Master Payroll / Contract sub-section
// Returns the new Y position
function drawEmployeeBlock(doc, emp, order, dayLabels, startY, pageH) {
  const pw   = doc.internal.pageSize.getWidth();
  const usableW = pw - PAGE_MARGIN * 2;
  const labelW  = 52;
  const totalW  = 18;
  const dayW    = (usableW - labelW - totalW) / 7;

  const BLOCK_MIN_HEIGHT = 10; // ensure we don't orphan the header row
  if (startY + BLOCK_MIN_HEIGHT > pageH - 15) {
    doc.addPage();
    startY = 18;
  }

  // Employee header row
  doc.setFillColor(...HEADER_BG);
  doc.rect(PAGE_MARGIN, startY, usableW, LINE_H + 1, 'F');
  doc.setFontSize(FONT_BODY);
  doc.setFont('helvetica', 'bold');
  const empHeaderText = `${emp.empl_code} – ${emp.empl_name} (${emp.trade || ''})`;
  doc.text(
    truncateText(doc, empHeaderText, labelW - 3),
    PAGE_MARGIN + 1, startY + LINE_H - 0.5
  );
  // Day column headers
  dayLabels.forEach((lbl, i) => {
    const x = PAGE_MARGIN + labelW + dayW * i + dayW / 2;
    doc.text(lbl, x, startY + LINE_H - 0.5, { align: 'center' });
  });
  doc.text('TOTAL', PAGE_MARGIN + labelW + dayW * 7 + totalW / 2, startY + LINE_H - 0.5, { align: 'center' });
  let y = startY + LINE_H + 1;

  // Row types
  const rowTypes = [
    { key: 'reg',     label: 'Regular',             money: false },
    { key: 't12',     label: 'Time-and-a-half',      money: false },
    { key: 'dbl',     label: 'Double-time',          money: false },
    { key: 'r_and_b', label: 'Room and board',       money: true  },
    { key: 'travel_2',label: 'Travel allowance',     money: true  },
  ];

  rowTypes.forEach(rt => {
    let total = 0;
    const dayValues = order.map(dow => {
      const d = emp.days.get(dow);
      const v = d ? num(d[rt.key]) : 0;
      total += v;
      return v;
    });
    if (total === 0) return; // skip empty rows

    if (y + LINE_H > pageH - 15) {
      doc.addPage();
      y = 18;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONT_BODY);
    doc.text(rt.label, PAGE_MARGIN + 2, y + LINE_H - 1);

    dayValues.forEach((v, i) => {
      if (v !== 0) {
        const x = PAGE_MARGIN + labelW + dayW * i + dayW / 2;
        doc.text(rt.money ? fmtMoney(v) : fmtNum(v), x, y + LINE_H - 1, { align: 'center' });
      }
    });
    // Total
    const totalX = PAGE_MARGIN + labelW + dayW * 7 + totalW / 2;
    doc.setFont('helvetica', 'bold');
    doc.text(rt.money ? fmtMoney(total) : fmtNum(total), totalX, y + LINE_H - 1, { align: 'center' });
    doc.setFont('helvetica', 'normal');

    y += LINE_H;
  });

  // Comment notes (day-off lines)
  emp.comments.forEach(c => {
    if (!c.text) return;
    if (y + LINE_H > pageH - 15) { doc.addPage(); y = 18; }
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(FONT_BODY - 0.5);
    doc.setTextColor(80);
    doc.text(`${fmtDateLong(c.date)}: ${c.text}`, PAGE_MARGIN + 2, y + LINE_H - 1);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0);
    y += LINE_H;
  });

  y += 2; // small gap between employees
  return y;
}

// 2. MASTER PAYROLL REPORT
async function generateMasterPayrollReport(rows, forLabel, dateRange, isUS) {
  const order     = isUS ? US_DAY_ORDER : CA_DAY_ORDER;
  const dayLabels = order.map(d => DAY_ABBR[d]);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageH = doc.internal.pageSize.getHeight();
  let y = await addPageHeader(doc, 'MASTER PAYROLL REPORT', forLabel, dateRange);
  y = drawTotalsRow(doc, rows, y);

  const { empMap } = buildEmployeeWeekData(rows, isUS);

  // Sort employees alphabetically by name
  const employees = Array.from(empMap.values()).sort((a, b) =>
    (a.empl_name || '').localeCompare(b.empl_name || '')
  );

  employees.forEach(emp => {
    y = drawEmployeeBlock(doc, emp, order, dayLabels, y, pageH);
  });

  addPageNumbers(doc);
  return doc;
}

// 3. OP CODE SUMMARY REPORT
async function generateOpCodeSummaryReport(rows, forLabel, dateRange) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  let startY = await addPageHeader(doc, 'OP CODE SUMMARY REPORT', forLabel, dateRange);
  startY = drawTotalsRow(doc, rows, startY);

  // Use shared buildContractKey helper

  const accum = () => ({ reg: 0, t12: 0, dbl: 0, travel: 0, height: 0, r_and_b: 0, travel_2: 0, bus: 0, meals: 0 });
  const addRow = (t, r) => {
    t.reg     += num(r.reg);     t.t12     += num(r.t12);
    t.dbl     += num(r.dbl);     t.travel  += num(r.travel);
    t.height  += num(r.height);  t.r_and_b += num(r.r_and_b);
    t.travel_2+= num(r.travel_2);t.bus     += num(r.bus);
    t.meals   += num(r.meals);
  };
  const fmtTotals = t => ({
    reg: fmtNum(t.reg), t12: fmtNum(t.t12), dbl: fmtNum(t.dbl),
    travel: fmtNum(t.travel), height: fmtNum(t.height),
    r_and_b: fmtMoney(t.r_and_b), travel_2: fmtMoney(t.travel_2),
    bus: fmtMoney(t.bus), meals: fmtMoney(t.meals),
  });

  // Group: contract key → empl_code → op_code → aggregated totals
  const contractMap = new Map();
  rows.forEach(r => {
    const ck = buildContractKey(r);
    if (!contractMap.has(ck)) contractMap.set(ck, { rows: [], byEmpl: new Map() });
    const cEntry = contractMap.get(ck);
    cEntry.rows.push(r);

    const ek = r.empl_code || '';
    if (!cEntry.byEmpl.has(ek)) {
      cEntry.byEmpl.set(ek, {
        empl_code: r.empl_code || '',
        empl_name: r.empl_name || '',
        trade:     r.trade || '',
        byOpCode:  new Map(),
      });
    }
    const eEntry = cEntry.byEmpl.get(ek);

    const ok = r.op_code || '';
    if (!eEntry.byOpCode.has(ok)) {
      eEntry.byOpCode.set(ok, { op_code: ok, shift: fmtShift(r.shift), totals: accum(), comments: [] });
    }
    const oEntry = eEntry.byOpCode.get(ok);
    addRow(oEntry.totals, r);
    if (r.comment) oEntry.comments.push(r.comment);
  });

  const tableBody = [];

  Array.from(contractMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([ck, { rows: cRows, byEmpl }]) => {
      // Contract-level ALL rollup
      const cTotals = accum();
      cRows.forEach(r => addRow(cTotals, r));
      tableBody.push({
        contract:  ck,
        empl_code: '—',
        empl_name: 'ALL',
        trade:     'ALL',
        op_code:   '',
        shift:     '',
        ...fmtTotals(cTotals),
        _isTotal: true,
      });

      // Employees sorted alphabetically
      Array.from(byEmpl.values())
        .sort((a, b) => a.empl_name.localeCompare(b.empl_name))
        .forEach(emp => {
          const opCodes = Array.from(emp.byOpCode.values())
            .sort((a, b) => a.op_code.localeCompare(b.op_code));
          const multiOp = opCodes.length > 1;

          // Per-employee ALL row — only when employee worked multiple op codes
          if (multiOp) {
            const eTotals = accum();
            opCodes.forEach(o => addRow(eTotals, o.totals));
            tableBody.push({
              contract:  '',
              empl_code: emp.empl_code,
              empl_name: emp.empl_name,
              trade:     emp.trade,
              op_code:   'ALL',
              shift:     '',
              ...fmtTotals(eTotals),
              _isTotal: false,
              _isEmpTotal: true,
            });
          }

          // Individual op code rows — always show employee name (matching original format)
          opCodes.forEach(o => {
            tableBody.push({
              contract:  '',
              empl_code: emp.empl_code,
              empl_name: emp.empl_name,
              trade:     emp.trade,
              op_code:   o.op_code,
              shift:     o.shift,
              ...fmtTotals(o.totals),
              _isTotal:    false,
              _isEmpTotal: false,
              _comment:    o.comments.length ? o.comments.join('; ') : null,
            });
          });
        });
    });

  autoTable(doc, {
    startY,
    columns: [
      { header: 'Contract',  dataKey: 'contract' },
      { header: 'Empl Code', dataKey: 'empl_code' },
      { header: 'Employee Name', dataKey: 'empl_name' },
      { header: 'Trade',     dataKey: 'trade' },
      { header: 'OP Code',   dataKey: 'op_code' },
      { header: 'Shift',     dataKey: 'shift' },
      { header: 'Reg',       dataKey: 'reg' },
      { header: 'T12',       dataKey: 't12' },
      { header: 'Dbl',       dataKey: 'dbl' },
      { header: 'Travel',    dataKey: 'travel' },
      { header: 'Height',    dataKey: 'height' },
      { header: 'R&B',       dataKey: 'r_and_b' },
      { header: 'Travel$',   dataKey: 'travel_2' },
      { header: 'Bus',       dataKey: 'bus' },
      { header: 'Meals',     dataKey: 'meals' },
    ],
    body: tableBody,
    theme: 'grid',
    styles:      { fontSize: 6.5, cellPadding: 1.5, overflow: 'linebreak' },
    headStyles:  { fillColor: HEADER_BG, textColor: 0, fontStyle: 'bold', fontSize: 7 },
    columnStyles: {
      contract:  { cellWidth: 24 },
      empl_code: { cellWidth: 12 },
      empl_name: { cellWidth: 30 },
      trade:     { cellWidth: 14 },
      op_code:   { cellWidth: 14 },
      shift:     { cellWidth: 10 },
    },
    didParseCell(data) {
      if (data.section === 'body') {
        if (data.row.raw._isTotal) {
          data.cell.styles.fillColor = TOTAL_BG;
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.row.raw._isEmpTotal) {
          data.cell.styles.fillColor = [235, 235, 235];
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.row.raw._comment && data.column.dataKey === 'op_code') {
          data.cell.text = [
            data.cell.text[0] || '',
            ...(data.cell.text.slice(1)),
            `  >> ${data.row.raw._comment}`,
          ];
          data.cell.styles.fontStyle = 'italic';
        }
      }
    },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });

  addPageNumbers(doc);
  return doc;
}

// 4. CONTRACT REPORT
async function generateContractReport(rows, forLabel, dateRange, isUS) {
  const order     = isUS ? US_DAY_ORDER : CA_DAY_ORDER;
  const dayLabels = order.map(d => DAY_ABBR[d]);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageH = doc.internal.pageSize.getHeight();
  let y = await addPageHeader(doc, 'CONTRACT REPORT', forLabel, dateRange);
  y = drawTotalsRow(doc, rows, y);

  const pw = doc.internal.pageSize.getWidth();

  // Group by contract key (job_number + discipline_code + optional extra)
  const contractMap = new Map();
  rows.forEach(r => {
    const c = buildContractKey(r);
    if (!contractMap.has(c)) contractMap.set(c, []);
    contractMap.get(c).push(r);
  });

  Array.from(contractMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([contract, cRows], cIdx) => {
      // Page break between contracts (not before the first)
      if (cIdx > 0) {
        doc.addPage();
        y = 18;
      }

      // Contract header block
      if (y + 30 > pageH - 15) { doc.addPage(); y = 18; }

      const jobName  = cRows[0]?.job_name || '';
      const ctTotals = { reg: 0, t12: 0, dbl: 0, travel: 0, height: 0, r_and_b: 0, travel_2: 0, bus: 0, meals: 0 };
      cRows.forEach(r => {
        ctTotals.reg     += num(r.reg);
        ctTotals.t12     += num(r.t12);
        ctTotals.dbl     += num(r.dbl);
        ctTotals.travel  += num(r.travel);
        ctTotals.height  += num(r.height);
        ctTotals.r_and_b += num(r.r_and_b);
        ctTotals.travel_2+= num(r.travel_2);
        ctTotals.bus     += num(r.bus);
        ctTotals.meals   += num(r.meals);
      });

      doc.setFillColor(...TOTAL_BG);
      doc.rect(PAGE_MARGIN, y, pw - PAGE_MARGIN * 2, LINE_H, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FONT_BODY + 0.5);
      doc.text('TOTAL FOR CONTRACT', PAGE_MARGIN + 1, y + LINE_H - 1);
      y += LINE_H;

      doc.setFontSize(FONT_SUB);
      doc.text(`#${contract}`, PAGE_MARGIN + 1, y + LINE_H - 0.5);
      y += LINE_H;

      if (jobName) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(FONT_BODY);
        // Wrap long job names
        const lines = doc.splitTextToSize(jobName, pw - PAGE_MARGIN * 2 - 4);
        lines.forEach(line => {
          doc.text(line, PAGE_MARGIN + 1, y + LINE_H - 0.5);
          y += LINE_H;
        });
      }

      doc.text(dateRange, PAGE_MARGIN + 1, y + LINE_H - 0.5);
      y += LINE_H + 1;

      // Contract totals row
      y = drawTotalsRow(doc, cRows, y);

      // Group by op code
      const opMap = new Map();
      cRows.forEach(r => {
        const op = r.op_code || '0';
        if (!opMap.has(op)) opMap.set(op, []);
        opMap.get(op).push(r);
      });

      const hasMultipleOpCodes = opMap.size > 1 || (opMap.size === 1 && !opMap.has('0'));

      Array.from(opMap.entries())
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
        .forEach(([opCode, opRows]) => {
          // Op code header (only when meaningful — skip trivial "0" if single op code with no label)
          if (hasMultipleOpCodes) {
            if (y + 12 > pageH - 15) { doc.addPage(); y = 18; }

            doc.setFillColor(...HEADER_BG);
            doc.rect(PAGE_MARGIN, y, pw - PAGE_MARGIN * 2, LINE_H, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(FONT_BODY);
            const opDesc = opRows[0]?.op_code_description || '';
            const opHeader = opDesc
              ? `TOTAL FOR OP CODE  #${opCode} — ${opDesc} — for #${contract}`
              : `TOTAL FOR OP CODE  #${opCode} — for #${contract}`;
            doc.text(opHeader, PAGE_MARGIN + 1, y + LINE_H - 0.5);
            y += LINE_H;
            y = drawTotalsRow(doc, opRows, y);
          }

          // Per-employee day grids
          const empMap2 = new Map();
          opRows.forEach(r => {
            const k = r.empl_code || r.empl_name;
            if (!empMap2.has(k)) {
              empMap2.set(k, {
                empl_code: r.empl_code,
                empl_name: r.empl_name,
                trade: r.trade,
                days: new Map(),
                comments: [],
              });
            }
            const emp = empMap2.get(k);
            if (r.date) {
              const dt  = parseLocalDate(r.date);
              const dow = dt.getDay();
              if (!emp.days.has(dow)) emp.days.set(dow, { reg: 0, t12: 0, dbl: 0, r_and_b: 0, travel_2: 0 });
              const d = emp.days.get(dow);
              d.reg     += num(r.reg);
              d.t12     += num(r.t12);
              d.dbl     += num(r.dbl);
              d.r_and_b += num(r.r_and_b);
              d.travel_2 += num(r.travel_2);
            }
            if (r.comment) emp.comments.push({ date: r.date, text: r.comment });
          });

          Array.from(empMap2.values())
            .sort((a, b) => (a.empl_name || '').localeCompare(b.empl_name || ''))
            .forEach(emp => {
              y = drawEmployeeBlock(doc, emp, order, dayLabels, y, pageH);
            });
        });
    });

  addPageNumbers(doc);
  return doc;
}

// ─── MultiSelectDropdown ─────────────────────────────────────────────────────
// options: [{ value, label }]   selected: string[]   onChange(newSelected)

const MultiSelectDropdown = ({ label, options, selected, onChange }) => {
  const [isOpen, setIsOpen]   = useState(false);
  const [search, setSearch]   = useState('');
  const containerRef          = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (value) =>
    onChange(selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value]
    );

  const triggerLabel =
    selected.length === 0 ? `All (${options.length})`
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label ?? '1 selected')
      : `${selected.length} selected`;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Trigger row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <button
          type="button"
          onClick={() => setIsOpen(v => !v)}
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.42rem 0.7rem',
            border: `1px solid ${selected.length > 0 ? '#dc2626' : '#d1d5db'}`,
            borderRadius: '5px',
            backgroundColor: selected.length > 0 ? '#fef2f2' : 'white',
            color: selected.length > 0 ? '#dc2626' : '#374151',
            fontSize: '0.875rem',
            cursor: 'pointer',
            textAlign: 'left',
            fontWeight: selected.length > 0 ? '500' : '400',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {triggerLabel}
          </span>
          <span style={{ marginLeft: '0.5rem', flexShrink: 0, fontSize: '0.65rem' }}>
            {isOpen ? '▲' : '▼'}
          </span>
        </button>
        {selected.length > 0 && (
          <button
            type="button"
            title="Clear all"
            onClick={() => onChange([])}
            style={{
              background: 'none', border: '1px solid #d1d5db', borderRadius: '5px',
              padding: '0.42rem 0.5rem', cursor: 'pointer', color: '#6b7280', fontSize: '0.8rem',
            }}
          >✕</button>
        )}
      </div>

      {/* Selected tags row */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.35rem' }}>
          {selected.map(v => {
            const opt = options.find(o => o.value === v);
            if (!opt) return null;
            return (
              <span
                key={v}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                  padding: '0.18rem 0.5rem', borderRadius: '10px',
                  backgroundColor: '#fee2e2', color: '#b91c1c',
                  fontSize: '0.75rem', fontWeight: '500',
                }}
              >
                {opt.label}
                <button
                  type="button"
                  onClick={() => toggle(v)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#b91c1c', lineHeight: 1, fontSize: '0.7rem' }}
                >✕</button>
              </span>
            );
          })}
        </div>
      )}

      {/* Dropdown panel */}
      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 200,
          backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        }}>
          {/* Search */}
          <div style={{ padding: '0.5rem 0.5rem 0.3rem' }}>
            <input
              autoFocus
              type="text"
              placeholder={`Search ${label.toLowerCase()}…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '0.35rem 0.6rem', border: '1px solid #d1d5db',
                borderRadius: '4px', fontSize: '0.8rem', outline: 'none',
              }}
            />
          </div>
          {/* Select visible / Clear all */}
          <div style={{
            padding: '0.2rem 0.75rem 0.35rem', borderBottom: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'space-between',
          }}>
            <button
              type="button"
              onClick={() => onChange([...new Set([...selected, ...filtered.map(o => o.value)])])}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '0.72rem', padding: 0 }}
            >
              Select all visible ({filtered.length})
            </button>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => { onChange([]); setSearch(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.72rem', padding: 0 }}
              >
                Clear all ({selected.length})
              </button>
            )}
          </div>
          {/* Option list */}
          <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '0.75rem', color: '#9ca3af', fontSize: '0.8rem', textAlign: 'center' }}>
                No matches
              </div>
            ) : filtered.map(o => {
              const isSel = selected.includes(o.value);
              return (
                <label
                  key={o.value}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.55rem',
                    padding: '0.42rem 0.75rem', cursor: 'pointer',
                    backgroundColor: isSel ? '#fef2f2' : 'transparent',
                    borderBottom: '1px solid #f3f4f6',
                    fontSize: '0.83rem',
                    color: isSel ? '#dc2626' : '#374151',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggle(o.value)}
                    style={{ accentColor: '#dc2626', flexShrink: 0 }}
                  />
                  {o.label}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const TimesheetReports = ({ onBack }) => {
  const { instance, accounts } = useMsal();

  const [showHelp,     setShowHelp]     = useState(false);
  const [activeTab,    setActiveTab]    = useState('reports'); // 'reports' | 'missing'
  const [reportType,   setReportType]   = useState('details');
  const [startDate,    setStartDate]    = useState('');
  const [endDate,      setEndDate]      = useState('');
  const [divisions,    setDivisions]    = useState([]);
  const [projects,     setProjects]     = useState([]);
  const [employees,    setEmployees]    = useState([]);
  const [selDivisions, setSelDivisions] = useState([]);
  const [selProjects,  setSelProjects]  = useState([]);
  const [selEmployees, setSelEmployees] = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [filterLoading,setFilterLoading]= useState(true);
  const [error,        setError]        = useState(null);
  const [lastGenerated,setLastGenerated]= useState(null);
  const [submitted,    setSubmitted]    = useState(false);

  // Missing Timesheets state
  const [missingStart,      setMissingStart]      = useState('');
  const [missingEnd,        setMissingEnd]        = useState('');
  const [missingDivisionId, setMissingDivisionId] = useState('');
  const [missingResults,    setMissingResults]    = useState(null);
  const [missingLoading,    setMissingLoading]    = useState(false);
  const [missingError,      setMissingError]      = useState(null);
  const [missingDivisions,  setMissingDivisions]  = useState([]);

  const handleMissingSearch = useCallback(async () => {
    if (!missingStart || !missingEnd) {
      setMissingError('Start date and end date are required.');
      return;
    }
    setMissingError(null);
    setMissingLoading(true);
    try {
      const params = new URLSearchParams({ startDate: missingStart, endDate: missingEnd });
      if (missingDivisionId) params.append('divisionId', missingDivisionId);
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/actual-spent/missing-timesheets?${params}`);
      if (res.data.success) {
        setMissingResults(res.data);
        if (res.data.divisions && res.data.divisions.length > 0) {
          setMissingDivisions(res.data.divisions);
        }
      }
    } catch (e) {
      setMissingError(e?.response?.data?.message || 'Failed to fetch missing timesheets.');
    } finally {
      setMissingLoading(false);
    }
  }, [instance, accounts, missingStart, missingEnd, missingDivisionId]);

  // Populate filter dropdowns on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedApiCall(instance, accounts, 'GET', '/actual-spent/report-filters');
        if (res.data.success) {
          setDivisions(res.data.divisions || []);
          setProjects(res.data.projects  || []);
          setEmployees(res.data.employees|| []);
        }
      } catch (e) {
        setError('Failed to load filter options.');
      } finally {
        setFilterLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Projects visible in the project dropdown (filtered by selected divisions)
  const visibleProjects = selDivisions.length === 0
    ? projects
    : projects.filter(p => selDivisions.includes(String(p.division_id)));

  // Date validation errors (only shown after first generate attempt)
  const dateErrors = useMemo(() => {
    if (!submitted) return {};
    const errs = validateSchema(
      { startDate, endDate },
      {
        startDate: [dateRequired('Start date is required')],
        endDate:   [dateRequired('End date is required')],
      }
    );
    if (!errs.endDate && startDate && endDate && endDate < startDate) {
      errs.endDate = 'End date must be on or after start date';
    }
    return errs;
  }, [submitted, startDate, endDate]);

  const handleGenerate = useCallback(async () => {
    setSubmitted(true);
    if (!startDate || !endDate || (startDate && endDate && startDate > endDate)) {
      setError(null); // clear API error — inline messages handle date errors
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const params = new URLSearchParams({ startDate, endDate });
      if (selDivisions.length)  params.set('divisionIds', selDivisions.join(','));
      if (selProjects.length)   params.set('projectIds',  selProjects.join(','));
      if (selEmployees.length)  params.set('empCodes',    selEmployees.join(','));

      const res = await authenticatedApiCall(instance, accounts, 'GET', `/actual-spent/report-data?${params.toString()}`);
      if (!res.data.success) throw new Error(res.data.message || 'Unknown error');

      const rows = res.data.data || [];
      if (rows.length === 0) {
        setError('No data found for the selected filters and date range.');
        return;
      }

      const forLabel  = buildHeaderLabel(selDivisions, selProjects, divisions, projects);
      const dateRange = `${fmtDate(startDate)} – ${fmtDate(endDate)}`;
      const singleJob = selProjects.length === 1
        ? (projects.find((p) => String(p.id) === String(selProjects[0]))?.job_number || rows[0]?.job_number)
        : (selProjects.length === 0 && rows[0]?.job_number && new Set(rows.map((r) => r.job_number).filter(Boolean)).size === 1
          ? rows[0].job_number
          : null);
      _timesheetLetterhead = await loadLetterhead({ instance, accounts, jobNumber: singleJob || undefined });

      const saveTs = (doc, slug) => {
        doc.save(exportFilename({ job: singleJob || 'portal', reportSlug: slug, ext: 'pdf' }));
      };

      // Split rows by employee payroll_group so each group gets the correct week ordering.
      // payroll_group ('CA', 'US', or null) comes from lt_employee, not the project division,
      // so a US employee working on a CA project is still correctly placed in the US group.
      // Rows with null payroll_group fall back to the project's country_code, then default CA.
      const caRows = rows.filter(r =>
        (r.payroll_group || r.country_code || 'CA') !== 'US'
      );
      const usRows = rows.filter(r =>
        (r.payroll_group || r.country_code || 'CA') === 'US'
      );

      // Master Payroll and Contract reports use weekly day-grid columns (Sun–Sat vs Mon–Sun).
      // When both groups are present, generate two separate PDFs so neither group gets
      // the wrong column ordering.
      const needsSplit = (reportType === 'master' || reportType === 'contract')
                        && caRows.length > 0 && usRows.length > 0;

      if (needsSplit) {
        const groups = [
          { suffix: 'CA', groupRows: caRows, isUS: false },
          { suffix: 'US', groupRows: usRows, isUS: true  },
        ];
        for (const { suffix, groupRows, isUS } of groups) {
          const groupLabel = `${forLabel} (${suffix})`;
          const doc = reportType === 'master'
            ? await generateMasterPayrollReport(groupRows, groupLabel, dateRange, isUS)
            : await generateContractReport(groupRows, groupLabel, dateRange, isUS);
          saveTs(doc, `${reportType}_${suffix}`);
        }
        setLastGenerated({
          type: REPORT_TYPES.find(t => t.id === reportType)?.label,
          rows: rows.length,
          filename: '2 files downloaded (CA + US)',
        });
      } else {
        // Single group — use the actual group's ordering rather than a majority vote
        const isUS = usRows.length > caRows.length;
        let doc;
        if      (reportType === 'details')  doc = await generateDetailsReport(rows, forLabel, dateRange);
        else if (reportType === 'master')   doc = await generateMasterPayrollReport(rows, forLabel, dateRange, isUS);
        else if (reportType === 'opcode')   doc = await generateOpCodeSummaryReport(rows, forLabel, dateRange);
        else                                doc = await generateContractReport(rows, forLabel, dateRange, isUS);
        const filename = exportFilename({ job: singleJob || 'portal', reportSlug: reportType, ext: 'pdf' });
        doc.save(filename);
        setLastGenerated({
          type: REPORT_TYPES.find(t => t.id === reportType)?.label,
          rows: rows.length,
          filename,
        });
      }
    } catch (e) {
      setError(`Failed to generate report: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [reportType, startDate, endDate, selDivisions, selProjects, selEmployees, divisions, projects]);

  // ── styles ──────────────────────────────────────────────────────────────────
  const sectionStyle = {
    marginBottom: '1rem',
  };
  const labelStyle = {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.4rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  };
  const inputStyle = {
    padding: '0.4rem 0.6rem',
    border: '1px solid #d1d5db',
    borderRadius: '5px',
    fontSize: '0.875rem',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            onClick={onBack}
            style={{
              background: 'none', border: '1px solid #d1d5db', borderRadius: '5px',
              padding: '0.25rem 0.6rem', cursor: 'pointer', fontSize: '0.85rem', color: '#374151',
            }}
          >
            ← Back
          </button>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Timesheet Reports</h2>
          <button
            type="button"
            onClick={() => setShowHelp(true)}
            style={{
              padding: '6px 10px', backgroundColor: '#0f766e', color: 'white',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600,
            }}
          >
            ❓ Help
          </button>
        </div>
        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {[{ id: 'reports', label: '📄 Reports' }, { id: 'missing', label: '🔍 Missing Timesheets' }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding: '0.35rem 0.85rem', borderRadius: '6px', border: '1px solid',
              borderColor: activeTab === tab.id ? '#6366f1' : '#d1d5db',
              background: activeTab === tab.id ? '#6366f1' : 'white',
              color: activeTab === tab.id ? 'white' : '#374151',
              cursor: 'pointer', fontSize: '0.825rem', fontWeight: activeTab === tab.id ? 600 : 400,
            }}>{tab.label}</button>
          ))}
        </div>
      </div>

      {/* Missing Timesheets Tab */}
      {activeTab === 'missing' && (
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          <p style={{ color: '#6b7280', marginTop: 0, fontSize: '0.875rem' }}>
            Find active employees who have <strong>no timesheet entries</strong> for a given date range.
          </p>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#6b7280', marginBottom: 3 }}>Start Date *</label>
              <input type="date" value={missingStart} onChange={e => setMissingStart(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#6b7280', marginBottom: 3 }}>End Date *</label>
              <input type="date" value={missingEnd} min={missingStart} onChange={e => setMissingEnd(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#6b7280', marginBottom: 3 }}>Division (optional)</label>
              <select value={missingDivisionId} onChange={e => setMissingDivisionId(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem', minWidth: '160px' }}>
                <option value="">All Divisions</option>
                {(missingDivisions.length > 0 ? missingDivisions : divisions).map(d => (
                  <option key={d.id} value={d.id}>{d.division_name} {d.division_number ? `(${d.division_number})` : ''}</option>
                ))}
              </select>
            </div>
            <button onClick={handleMissingSearch} disabled={missingLoading}
              style={{ padding: '7px 18px', borderRadius: '4px', border: 'none', background: '#6366f1', color: 'white', cursor: missingLoading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
              {missingLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
          {missingError && <p style={{ color: '#dc3545', fontSize: '0.875rem' }}>{missingError}</p>}
          {missingResults && (
            <>
              <p style={{ fontSize: '0.875rem', color: '#495057', marginBottom: '0.75rem' }}>
                <strong>{missingResults.count}</strong> employee{missingResults.count !== 1 ? 's' : ''} with no entries
                between <strong>{missingResults.period?.startDate}</strong> and <strong>{missingResults.period?.endDate}</strong>.
              </p>
              {missingResults.count === 0 ? (
                <p style={{ color: '#28a745', fontWeight: 500 }}>✓ All active employees have timesheets for this period.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      {['Emp Code', 'Name', 'Trade', 'Division', 'Email'].map(h => (
                        <th key={h} style={{ padding: '0.5rem 0.75rem', borderBottom: '2px solid #dee2e6', textAlign: 'left', fontWeight: 600, color: '#495057', fontSize: '0.8rem' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {missingResults.data.map((emp, i) => (
                      <tr key={emp.emp_code} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#f8f9fa' }}>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #dee2e6' }}>{emp.emp_code}</td>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #dee2e6', fontWeight: 500 }}>{emp.emp_name}</td>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #dee2e6' }}>{emp.trade || '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #dee2e6' }}>{emp.division_name || '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #dee2e6' }}>
                          {emp.empl_email
                            ? <a href={`mailto:${emp.empl_email}`} style={{ color: '#6366f1' }}>{emp.empl_email}</a>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
      <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
        {filterLoading ? (
          <p style={{ color: '#6b7280' }}>Loading filters…</p>
        ) : (
          <>
            {/* Report type */}
            <div style={sectionStyle}>
              <span style={labelStyle}>Report Type</span>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {REPORT_TYPES.map(rt => (
                  <label key={rt.id} style={{
                    display: 'flex', alignItems: 'center', gap: '0.35rem',
                    padding: '0.4rem 0.8rem',
                    border: `1px solid ${reportType === rt.id ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: reportType === rt.id ? '#fef2f2' : 'white',
                    fontSize: '0.875rem',
                    fontWeight: reportType === rt.id ? '600' : '400',
                    color: reportType === rt.id ? '#dc2626' : '#374151',
                    transition: 'all 0.15s ease',
                  }}>
                    <input
                      type="radio"
                      name="reportType"
                      value={rt.id}
                      checked={reportType === rt.id}
                      onChange={() => setReportType(rt.id)}
                      style={{ display: 'none' }}
                    />
                    {rt.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Date range */}
            <div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: dateErrors.startDate ? '#dc2626' : '#d1d5db',
                  }}
                />
                {dateErrors.startDate && (
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#dc2626' }}>
                    {dateErrors.startDate}
                  </p>
                )}
              </div>
              <div>
                <label style={labelStyle}>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: dateErrors.endDate ? '#dc2626' : '#d1d5db',
                  }}
                />
                {dateErrors.endDate && (
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#dc2626' }}>
                    {dateErrors.endDate}
                  </p>
                )}
              </div>
            </div>

            {/* Division filter */}
            <div style={sectionStyle}>
              <span style={labelStyle}>Division (optional)</span>
              <MultiSelectDropdown
                label="divisions"
                options={divisions.map(d => ({
                  value: String(d.id),
                  label: `${d.division_number} – ${d.division_name} (${d.country_code})`,
                }))}
                selected={selDivisions}
                onChange={setSelDivisions}
              />
            </div>

            {/* Project filter */}
            <div style={sectionStyle}>
              <span style={labelStyle}>Project (optional)</span>
              <MultiSelectDropdown
                label="projects"
                options={visibleProjects.map(p => ({
                  value: String(p.id),
                  label: `${p.job_number} – ${p.job_name}`,
                }))}
                selected={selProjects}
                onChange={setSelProjects}
              />
            </div>

            {/* Employee filter */}
            <div style={sectionStyle}>
              <span style={labelStyle}>Employee (optional)</span>
              <MultiSelectDropdown
                label="employees"
                options={employees.map(e => ({
                  value: e.emp_code,
                  label: `${e.emp_code} – ${e.emp_name}${e.trade ? ` (${e.trade})` : ''}`,
                }))}
                selected={selEmployees}
                onChange={setSelEmployees}
              />
            </div>

            {/* Error */}
            {error && (
              <div style={{
                padding: '0.75rem 1rem',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#dc2626',
                fontSize: '0.875rem',
                marginBottom: '1rem',
              }}>
                {error}
              </div>
            )}

            {/* Last generated */}
            {lastGenerated && !error && (
              <div style={{
                padding: '0.75rem 1rem',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '6px',
                color: '#166534',
                fontSize: '0.875rem',
                marginBottom: '1rem',
              }}>
                ✓ Generated <strong>{lastGenerated.type}</strong> ({lastGenerated.rows} rows) → <code>{lastGenerated.filename}</code>
              </div>
            )}
          </>
        )}
      </div>
      )} {/* end activeTab === 'reports' */}

      {/* Footer — only shown on reports tab */}
      {activeTab === 'reports' && (
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
        padding: '1rem 1.5rem',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
      }}>
        <button
          onClick={onBack}
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            backgroundColor: 'white',
            color: '#374151',
            cursor: 'pointer',
            fontSize: '0.875rem',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleGenerate}
          disabled={loading || filterLoading || !startDate || !endDate}
          style={{
            padding: '0.5rem 1.25rem',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: loading ? '#9ca3af' : '#dc2626',
            color: 'white',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: '600',
          }}
        >
          {loading ? 'Generating…' : '📄 Generate PDF'}
        </button>
      </div>
      )} {/* end activeTab === 'reports' footer */}
      {showHelp && <TimeTrackerAdminHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
};

export default TimesheetReports;
